/**
 * Aktionen an einem Befund (A11). Knöpfe = `befund.aktionen` ∩ `befund.darf` (serverseitig per
 * canDoProjectAction). Dialoge mit Eingaben laufen über <Modal> mit closeOnBackdrop/closeOnEscape
 * = false — ein Fehlklick darf Datum und Begründung nicht verwerfen.
 */
import type { BefundAntwortArt, BefundAnzeige } from '@projektverwaltung/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../lib/api.js';
import { useToast } from '../../toast.js';
import { Modal } from '../modal.js';
import { BEFUNDE_QUERY_KEY } from './befund-basis.js';

function fehlertext(err: unknown): string | undefined {
  return err instanceof Error ? err.message : undefined;
}

/** Der Weg ins Projekt ist der Link in der Kopfzeile (befund-zeile.tsx), kein eigener Knopf. */
export function BefundAktionen({ befund }: { befund: BefundAnzeige }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [dialog, setDialog] = useState<'zurueckstellen' | 'nachhaken' | 'antworten' | null>(null);
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: BEFUNDE_QUERY_KEY });

  const briefing = useMutation({
    mutationFn: () => api.generateKiBriefing(befund.project_id),
    onSuccess: () => {
      toast.success(t('v2.befund.briefing_ok', 'Mission Briefing erzeugt'));
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['v2-briefings', befund.project_id] });
    },
    onError: (err) => toast.error(t('v2.befund.fehler', 'Aktion fehlgeschlagen'), fehlertext(err)),
  });
  const neuPruefen = useMutation({
    mutationFn: () => api.befundNeuPruefen(befund.project_id),
    onSuccess: (res) => {
      if (res.float_ok === false) {
        toast.push({
          kind: 'info',
          title: t(
            'v2.befund.neu_pruefen_ohne_float',
            'Neu geprüft — Float war nicht erreichbar, Float- und Budget-Befunde sind unverändert.',
          ),
        });
      } else {
        toast.success(t('v2.befund.neu_pruefen_ok', 'Neu geprüft'));
      }
      invalidate();
    },
    onError: (err) => toast.error(t('v2.befund.fehler', 'Aktion fehlgeschlagen'), fehlertext(err)),
  });

  if (befund.zustand === 'erledigt') return null;
  const hat = (a: BefundAnzeige['aktionen'][number]) => befund.aktionen.includes(a);

  return (
    <>
      {hat('briefing_erzeugen') && befund.darf.briefing_erzeugen && (
        <AktionKnopf onClick={() => briefing.mutate()} disabled={briefing.isPending}>
          {briefing.isPending
            ? t('v2.befund.aktion_briefing_laeuft', 'Briefing wird erzeugt…')
            : t('v2.befund.aktion_briefing', 'Mission Briefing erzeugen')}
        </AktionKnopf>
      )}
      {hat('neu_pruefen') && befund.darf.neu_pruefen && (
        <AktionKnopf onClick={() => neuPruefen.mutate()} disabled={neuPruefen.isPending}>
          {t('v2.befund.aktion_neu_pruefen', 'Jetzt neu prüfen')}
        </AktionKnopf>
      )}
      {hat('antworten') && befund.darf.antworten && (
        <AktionKnopf onClick={() => setDialog('antworten')}>
          {t('v2.befund.aktion_antworten', 'Antworten')}
        </AktionKnopf>
      )}
      {hat('zurueckstellen') && befund.darf.zurueckstellen && (
        <AktionKnopf onClick={() => setDialog('zurueckstellen')}>
          {t('v2.befund.aktion_zurueckstellen', 'Zurückstellen')}
        </AktionKnopf>
      )}
      {hat('nachhaken') && befund.darf.nachhaken && (
        <AktionKnopf onClick={() => setDialog('nachhaken')}>
          {t('v2.befund.aktion_nachhaken', 'Nachhaken')}
        </AktionKnopf>
      )}
      <ZurueckstellenDialog
        open={dialog === 'zurueckstellen'}
        befundId={befund.befund_id}
        onClose={() => setDialog(null)}
        onDone={invalidate}
      />
      <NachhakenDialog
        open={dialog === 'nachhaken'}
        befundId={befund.befund_id}
        onClose={() => setDialog(null)}
        onDone={invalidate}
      />
      <AntwortDialog
        open={dialog === 'antworten'}
        befund={befund}
        onClose={() => setDialog(null)}
        onDone={() => {
          invalidate();
          // Die Antwort setzt das KFML-Kennzeichen → Worklist und Projekt sofort frisch.
          void queryClient.invalidateQueries({ queryKey: ['v2-kfml-worklist'] });
          void queryClient.invalidateQueries({ queryKey: ['v2-project', befund.project_id] });
        }}
      />
    </>
  );
}

function AktionKnopf({
  children,
  onClick,
  disabled = false,
}: { children: ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="text-xs px-2.5 py-1 rounded-md border disabled:opacity-50"
      style={{
        background: 'var(--surface)',
        borderColor: 'var(--line-soft)',
        color: 'var(--ink-2)',
      }}
    >
      {children}
    </button>
  );
}

type DialogProps = { open: boolean; befundId: string; onClose: () => void; onDone: () => void };

const FELD_STIL = {
  background: 'var(--surface-2)',
  border: '1px solid var(--line-soft)',
  color: 'var(--ink)',
} as const;

function DialogRahmen({
  id,
  titel,
  text,
  children,
  bereit,
  sendet,
  onClose,
  onSubmit,
  submitLabel,
}: {
  id: string;
  titel: string;
  text: string;
  children: ReactNode;
  bereit: boolean;
  sendet: boolean;
  onClose: () => void;
  onSubmit: () => void;
  submitLabel: string;
}) {
  const { t } = useTranslation();
  return (
    <Modal
      open
      onClose={onClose}
      labelledBy={id}
      panelClassName="w-full max-w-md overflow-hidden"
      closeOnBackdrop={false}
      closeOnEscape={false}
    >
      <header className="px-5 py-4 border-b" style={{ borderColor: 'var(--line-soft)' }}>
        <h2 id={id} className="text-sm font-medium" style={{ color: 'var(--ink)' }}>
          {titel}
        </h2>
      </header>
      <div className="px-5 py-4 space-y-3">
        <p className="text-sm" style={{ color: 'var(--ink-2)' }}>
          {text}
        </p>
        {children}
      </div>
      <footer
        className="px-5 py-3 border-t flex items-center justify-end gap-2"
        style={{ borderColor: 'var(--line-soft)', background: 'var(--surface-2)' }}
      >
        <button
          type="button"
          onClick={onClose}
          disabled={sendet}
          className="text-xs px-3 py-1.5 rounded-md"
          style={{
            background: 'var(--surface)',
            color: 'var(--ink-2)',
            border: '1px solid var(--line-soft)',
          }}
        >
          {t('v2.befund.abbrechen', 'Abbrechen')}
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={!bereit || sendet}
          className="text-xs px-3 py-1.5 rounded-md disabled:opacity-50"
          style={{ background: 'var(--primary)', color: 'var(--on-primary)', border: 'none' }}
        >
          {submitLabel}
        </button>
      </footer>
    </Modal>
  );
}

function ZurueckstellenDialog({ open, befundId, onClose, onDone }: DialogProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const [bis, setBis] = useState('');
  const [grund, setGrund] = useState('');
  useEffect(() => {
    if (open) {
      setBis('');
      setGrund('');
    }
  }, [open]);
  const mutation = useMutation({
    mutationFn: () =>
      api.befundAktion(befundId, { art: 'zurueckstellen', bis, grund: grund.trim() }),
    onSuccess: () => {
      toast.success(t('v2.befund.zurueckstellen_ok', 'Befund zurückgestellt'));
      onDone();
      onClose();
    },
    onError: (err) => toast.error(t('v2.befund.fehler', 'Aktion fehlgeschlagen'), fehlertext(err)),
  });
  if (!open) return null;
  // Datum UND Begründung Pflicht (A3). Leerzeichen zählen nicht — das Schema prüft serverseitig dasselbe.
  const bereit = bis !== '' && grund.trim().length > 0;
  const morgen = new Date(Date.now() + 86_400_000).toISOString().slice(0, 10);
  return (
    <DialogRahmen
      id={`zurueckstellen-${befundId}`}
      titel={t('v2.befund.zurueckstellen_titel', 'Befund zurückstellen')}
      text={t(
        'v2.befund.zurueckstellen_text',
        'Der Befund verlässt bis zum Datum deinen Stapel. Trifft die Regel danach noch zu, ist er wieder offen.',
      )}
      bereit={bereit}
      sendet={mutation.isPending}
      onClose={onClose}
      onSubmit={() => mutation.mutate()}
      submitLabel={t('v2.befund.speichern', 'Speichern')}
    >
      <label className="block text-xs" style={{ color: 'var(--ink-3)' }}>
        {t('v2.befund.zurueckstellen_bis', 'Zurückstellen bis *')}
        <input
          type="date"
          min={morgen}
          value={bis}
          onChange={(e) => setBis(e.target.value)}
          className="mt-1 w-full text-sm px-2 py-1.5 rounded"
          style={FELD_STIL}
        />
      </label>
      <label className="block text-xs" style={{ color: 'var(--ink-3)' }}>
        {t('v2.befund.zurueckstellen_grund', 'Begründung *')}
        <textarea
          value={grund}
          onChange={(e) => setGrund(e.target.value)}
          rows={3}
          className="mt-1 w-full text-sm px-2 py-1.5 rounded resize-y"
          style={FELD_STIL}
        />
      </label>
    </DialogRahmen>
  );
}

function NachhakenDialog({ open, befundId, onClose, onDone }: DialogProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const [text, setText] = useState('');
  useEffect(() => {
    if (open) setText('');
  }, [open]);
  const mutation = useMutation({
    mutationFn: () =>
      api.befundAktion(
        befundId,
        text.trim() ? { art: 'nachhaken', text: text.trim() } : { art: 'nachhaken' },
      ),
    onSuccess: () => {
      toast.success(t('v2.befund.nachhaken_ok', 'Nachgehakt'));
      onDone();
      onClose();
    },
    onError: (err) => toast.error(t('v2.befund.fehler', 'Aktion fehlgeschlagen'), fehlertext(err)),
  });
  if (!open) return null;
  return (
    <DialogRahmen
      id={`nachhaken-${befundId}`}
      titel={t('v2.befund.nachhaken_titel', 'Nachhaken')}
      text={t(
        'v2.befund.nachhaken_text',
        'Schreibt einen Kommentar am Projekt und erwähnt die zuständige Person.',
      )}
      bereit
      sendet={mutation.isPending}
      onClose={onClose}
      onSubmit={() => mutation.mutate()}
      submitLabel={t('v2.befund.senden', 'Senden')}
    >
      <label className="block text-xs" style={{ color: 'var(--ink-3)' }}>
        {t('v2.befund.nachhaken_nachricht', 'Nachricht (optional)')}
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          className="mt-1 w-full text-sm px-2 py-1.5 rounded resize-y"
          style={FELD_STIL}
        />
      </label>
    </DialogRahmen>
  );
}

function AntwortDialog({
  open,
  befund,
  onClose,
  onDone,
}: {
  open: boolean;
  befund: BefundAnzeige;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [art, setArt] = useState<BefundAntwortArt | null>(null);
  const [datum, setDatum] = useState('');
  const [grund, setGrund] = useState('');
  useEffect(() => {
    if (open) {
      setArt(null);
      setDatum('');
      setGrund('');
    }
  }, [open]);
  const mutation = useMutation({
    mutationFn: () => {
      if (!art) throw new Error('keine_antwort');
      const g = grund.trim();
      return api.befundAktion(befund.befund_id, {
        art: 'antwort',
        antwort: art,
        ...(art !== 'schliessen' ? { datum } : {}),
        ...(g ? { grund: g } : {}),
      });
    },
    onSuccess: () => {
      toast.success(t('v2.befund.antwort_ok', 'Antwort gespeichert'));
      onDone();
      onClose();
    },
    onError: (err) => toast.error(t('v2.befund.fehler', 'Aktion fehlgeschlagen'), fehlertext(err)),
  });
  if (!open) return null;

  // A11: „kann geschlossen werden" nur bei in der PV abgeschlossenem Projekt — sonst gesperrt MIT Grund.
  const schliessenGesperrt = !befund.darf.antwort_schliessen;
  const bereit =
    art === 'schliessen'
      ? !schliessenGesperrt
      : art === 'verlaengern'
        ? datum !== ''
        : art === 'laeuft_weiter'
          ? datum !== '' && grund.trim().length > 0
          : false;
  const morgen = new Date(Date.now() + 86_400_000).toISOString().slice(0, 10);
  const name = `antwort-${befund.befund_id}`;
  const option = (wert: BefundAntwortArt, label: string, gesperrt = false, hinweis?: string) => (
    <label
      className="flex items-start gap-2 text-sm"
      style={{ color: gesperrt ? 'var(--ink-3)' : 'var(--ink)' }}
    >
      <input
        type="radio"
        name={name}
        checked={art === wert}
        disabled={gesperrt}
        onChange={() => setArt(wert)}
        className="mt-1"
      />
      <span>
        {label}
        {hinweis && (
          <span className="block text-xs" style={{ color: 'var(--ink-3)' }}>
            {hinweis}
          </span>
        )}
      </span>
    </label>
  );

  return (
    <DialogRahmen
      id={name}
      titel={t('v2.befund.antwort_titel', 'Schließ-Check beantworten')}
      text={t(
        'v2.befund.antwort_text',
        'Die ersten beiden Antworten setzen das KFML-Kennzeichen; der Befund geht dann an die kaufmännische Leitung.',
      )}
      bereit={bereit}
      sendet={mutation.isPending}
      onClose={onClose}
      onSubmit={() => mutation.mutate()}
      submitLabel={t('v2.befund.speichern', 'Speichern')}
    >
      <fieldset className="space-y-2">
        <legend className="sr-only">{t('v2.befund.antwort_legende', 'Antwort')}</legend>
        {option(
          'schliessen',
          t('v2.befund.antwort_schliessen', 'Kann geschlossen werden'),
          schliessenGesperrt,
          schliessenGesperrt
            ? t(
                'v2.befund.antwort_schliessen_gesperrt',
                'Erst möglich, wenn das Projekt in der PV abgeschlossen ist.',
              )
            : undefined,
        )}
        {option('verlaengern', t('v2.befund.antwort_verlaengern', 'Verlängern bis …'))}
        {option(
          'laeuft_weiter',
          t('v2.befund.antwort_laeuft_weiter', 'Läuft bewusst weiter bis …'),
        )}
      </fieldset>
      {(art === 'verlaengern' || art === 'laeuft_weiter') && (
        <label className="block text-xs" style={{ color: 'var(--ink-3)' }}>
          {t('v2.befund.antwort_datum', 'Datum *')}
          <input
            type="date"
            min={morgen}
            value={datum}
            onChange={(e) => setDatum(e.target.value)}
            className="mt-1 w-full text-sm px-2 py-1.5 rounded"
            style={FELD_STIL}
          />
        </label>
      )}
      {art !== null && (
        <label className="block text-xs" style={{ color: 'var(--ink-3)' }}>
          {art === 'laeuft_weiter'
            ? t('v2.befund.antwort_grund_pflicht', 'Begründung *')
            : t('v2.befund.antwort_kommentar', 'Kommentar (optional)')}
          <textarea
            value={grund}
            onChange={(e) => setGrund(e.target.value)}
            rows={3}
            className="mt-1 w-full text-sm px-2 py-1.5 rounded resize-y"
            style={FELD_STIL}
          />
        </label>
      )}
    </DialogRahmen>
  );
}
